import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SERVICES, PROJECTS, INDUSTRIES, VALUES, TESTIMONIALS, TRUST_ITEMS, STATS } from "@/data/site";
import { SectionHeader, Reveal, ParallaxImage, EditorialMarquee } from "@/components/ui-blocks";

// --- Trust Bar ---
export const TrustBar = () => (
  <section data-testid="trust-bar" className="relative border-y border-white/5 bg-obsidian-900/60">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-3">
      {TRUST_ITEMS.map((t) => (
        <div key={t} className="flex items-center gap-3 text-ink-200 text-[11px] font-medium uppercase tracking-[0.18em]">
          <span className="w-1.5 h-1.5 bg-accent block" />
          <span>{t}</span>
        </div>
      ))}
    </div>
  </section>
);

// --- Stats bar (numbers) ---
export const StatsBar = () => (
  <section data-testid="stats-bar" className="relative py-16 lg:py-24">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <div className="border-l border-white/10 pl-5 lg:pl-7">
            <div className="font-display text-5xl md:text-6xl lg:text-7xl leading-none text-ink-50">{s.value}</div>
            <div className="overline mt-3">{s.label}</div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

// --- Services Grid (exposed borders with hover reveal) ---
export const ServicesGrid = () => {
  return (
    <section data-testid="services-grid" className="relative py-24 lg:py-40">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 lg:mb-24 items-end">
          <div className="lg:col-span-7">
            <SectionHeader
              eyebrow="What We Build"
              title={<>Ten disciplines.<br />One accountable partner.</>}
            />
          </div>
          <div className="lg:col-span-5 lg:pl-8">
            <Reveal delay={0.2}>
              <p className="text-ink-400 text-base leading-relaxed">
                From ground-up industrial builds to fast-track tenant improvements, PHOS Management delivers every discipline under a single team — with the coordination that only comes from 25 years of doing it.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="border-t border-white/10">
          {SERVICES.map((svc, i) => (
            <Link
              to={`/services/${svc.slug}`}
              key={svc.slug}
              data-testid={`service-row-${svc.slug}`}
              className="svc-card group relative flex flex-col md:flex-row md:items-center gap-6 py-8 lg:py-10 border-b border-white/10 hover:bg-white/[0.02] transition-colors duration-500"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
              }}
            >
              <div className="svc-glow" aria-hidden />
              <div className="flex items-baseline gap-6 md:w-40 shrink-0">
                <span className="font-display text-2xl text-accent leading-none">{svc.number}</span>
              </div>
              <div className="flex-1 flex flex-col md:flex-row md:items-center md:gap-10">
                <h3 className="font-display text-3xl md:text-5xl lg:text-6xl uppercase tracking-tight leading-none flex-1 group-hover:translate-x-2 transition-transform duration-500">
                  {svc.title}
                </h3>
                <p className="text-ink-400 max-w-md text-sm leading-relaxed">{svc.short}</p>
              </div>
              <ArrowUpRight
                size={28}
                className="text-ink-600 group-hover:text-accent group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
              />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Featured Projects ---
export const FeaturedProjects = ({ limit = 5 }) => {
  const list = PROJECTS.slice(0, limit);
  return (
    <section data-testid="featured-projects" className="relative py-24 lg:py-40 bg-obsidian-950">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-8">
            <SectionHeader
              eyebrow="Selected Work"
              title={<>Projects with<br />receipts.</>}
              sub="A portfolio built over two decades: ground-up commercial, industrial expansions, multifamily community upgrades, and custom residential estates across Southeast Michigan."
            />
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/projects" data-testid="featured-projects-cta" className="btn-outline">
              All Projects <ArrowUpRight size={16} className="btn-arrow" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {list.map((p, i) => {
            const spans = [
              "col-span-12 lg:col-span-8 aspect-[16/10]",
              "col-span-12 lg:col-span-4 aspect-[4/5]",
              "col-span-12 md:col-span-6 lg:col-span-5 aspect-[5/6]",
              "col-span-12 md:col-span-6 lg:col-span-7 aspect-[16/10]",
              "col-span-12 aspect-[21/9]",
            ];
            return (
              <Reveal key={p.id} delay={i * 0.08} className={`${spans[i % spans.length]}`}>
                <Link
                  to="/projects"
                  data-testid={`featured-project-${p.id}`}
                  className="group relative block h-full overflow-hidden border border-white/10"
                >
                  <img
                    src={p.image}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover grayscale-hover group-hover:scale-105 transition-transform duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian-950 via-obsidian-950/20 to-transparent opacity-90 group-hover:opacity-70 transition-opacity duration-500" />
                  <div className="relative z-10 h-full flex flex-col justify-between p-6 lg:p-8">
                    <div className="flex items-center justify-between">
                      <span className="overline text-ink-200 bg-obsidian-950/60 backdrop-blur px-3 py-1 border border-white/10">
                        {p.industry}
                      </span>
                      <ArrowUpRight
                        size={22}
                        className="text-ink-200 opacity-0 -translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500"
                      />
                    </div>
                    <div>
                      <p className="overline text-ink-400 mb-2">{p.location} · {p.year}</p>
                      <h3 className="font-display text-3xl md:text-4xl lg:text-5xl uppercase leading-[0.9] text-ink-50">{p.name}</h3>
                      <p className="text-ink-400 text-sm mt-2">{p.scope}</p>
                    </div>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// --- Manifesto / Why PHOS ---
export const Manifesto = () => (
  <section data-testid="manifesto" className="relative py-24 lg:py-40 bg-obsidian-900/40 border-y border-white/5">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 mb-20 items-end">
        <div className="lg:col-span-8">
          <SectionHeader
            eyebrow="The PHOS Difference"
            title={<>Seven reasons owners<br />pick up the phone.</>}
          />
        </div>
        <div className="lg:col-span-4">
          <Reveal delay={0.2}>
            <p className="text-ink-400 text-sm leading-relaxed">
              Most of our work is repeat business. Once an owner works with PHOS, they don&apos;t bid the next one — they call us. Here&apos;s why.
            </p>
          </Reveal>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {VALUES.map((v, i) => (
          <Reveal key={v.n} delay={i * 0.06}>
            <div className="group relative border border-white/10 p-8 lg:p-10 h-full hover:border-accent transition-colors duration-500">
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-display text-5xl lg:text-6xl text-accent leading-none">{v.n}</span>
                <span className="w-8 h-px bg-white/20 group-hover:bg-accent group-hover:w-14 transition-all duration-500" />
              </div>
              <h3 className="font-heading text-2xl font-bold uppercase tracking-tight mb-4">{v.title}</h3>
              <p className="text-ink-400 text-sm leading-relaxed">{v.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

// --- Industries ---
export const IndustriesGrid = () => (
  <section data-testid="industries-grid" className="relative py-24 lg:py-40">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
        <div className="lg:col-span-8">
          <SectionHeader
            eyebrow="Industries Served"
            title={<>Nine markets.<br />One standard.</>}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-white/10">
        {INDUSTRIES.map((ind, i) => (
          <div
            key={ind.title}
            data-testid={`industry-${i}`}
            className="group relative p-8 lg:p-10 border-r border-b border-white/10 hover:bg-obsidian-900/60 transition-colors duration-500"
          >
            <div className="flex items-start justify-between mb-8">
              <span className="font-display text-4xl md:text-5xl text-ink-50 group-hover:text-accent transition-colors duration-500">
                {ind.count}
              </span>
              <span className="overline text-ink-600">
                {String(i + 1).padStart(2, "0")}
              </span>
            </div>
            <h3 className="font-heading text-xl font-bold uppercase tracking-tight">{ind.title}</h3>
            <p className="text-ink-400 text-sm mt-2">{ind.note}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// --- Testimonials ---
export const Testimonials = () => {
  const [i, setI] = React.useState(0);
  const t = TESTIMONIALS[i];
  return (
    <section data-testid="testimonials" className="relative py-24 lg:py-40 bg-obsidian-900/40 border-y border-white/5">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <SectionHeader eyebrow="Owner Voices" title={<>What clients say<br />after handover.</>} />
        </div>
        <div className="lg:col-span-8">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="font-display text-8xl lg:text-9xl text-accent leading-none block">&ldquo;</span>
            <blockquote className="font-heading text-2xl md:text-3xl lg:text-4xl leading-[1.25] text-ink-50 -mt-6">
              {t.quote}
            </blockquote>
            <div className="mt-10 flex items-center gap-4">
              <div className="w-10 h-px bg-accent" />
              <div>
                <div className="font-heading font-bold text-ink-50">{t.author}</div>
                <div className="overline mt-1">{t.title}</div>
              </div>
            </div>
          </motion.div>

          <div className="mt-12 flex items-center gap-4">
            {TESTIMONIALS.map((_, idx) => (
              <button
                key={idx}
                data-testid={`testimonial-dot-${idx}`}
                onClick={() => setI(idx)}
                className={`h-1 transition-all duration-500 ${idx === i ? "w-16 bg-accent" : "w-8 bg-white/15 hover:bg-white/30"}`}
                aria-label={`Testimonial ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Homepage editorial marquee ---
export const HomeMarquee = () => (
  <EditorialMarquee
    items={[
      "Commercial",
      "Industrial",
      "Multifamily",
      "Design-Build",
      "Construction Management",
      "Build-Outs",
    ]}
  />
);

// --- About preview (Home section) ---
export const AboutPreview = () => (
  <section data-testid="about-preview" className="relative py-24 lg:py-40">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-6">
        <ParallaxImage
          src="/brand/placeholder.jpg"
          alt="PHOS Management on-site"
          className="aspect-[4/5] border border-white/10"
        />
      </div>
      <div className="lg:col-span-6 lg:pl-10">
        <SectionHeader
          eyebrow="Est. 1999 · Riverview, MI"
          title={<>Twenty-five years<br />of showing up.</>}
        />
        <div className="mt-10 space-y-6 text-ink-400 text-base leading-relaxed max-w-xl">
          <p>
            PHOS Management started with a hammer, a pickup truck, and a promise: deliver every project on time, on budget, with zero excuses.
          </p>
          <p>
            Twenty-five years and 1,000+ projects later, that promise still runs every job site — backed by a team of senior superintendents, licensed PMs, and craftsmen who take pride in the work.
          </p>
        </div>
        <div className="mt-10">
          <Link to="/about" data-testid="about-preview-cta" className="btn-outline">
            About PHOS <ArrowUpRight size={16} className="btn-arrow" />
          </Link>
        </div>
      </div>
    </div>
  </section>
);
